<script lang="ts">
  import Icon from '$shared/atoms/Icon/Icon.svelte';
  import { IconType } from '$shared/constants/IconType';
  import ConfirmActions from '$shared/atoms/ConfirmActions/ConfirmActions.svelte';
  import Label from '$provider/constants/Label';
  import GenericInput from '$shared/molecules/GenericInput/GenericInput.svelte';
  import ResetAction from './partials/ResetAction.svelte';
  import type { EditableListRowModel } from '$provider/models/EditableListRowModel';
  import { onMount } from 'svelte';

  interface Props {
    option?: EditableListRowModel | null;
    active?: boolean;
    children?: import('svelte').Snippet;
    onevent?: (payload: { type: string; data?: any }) => void;
  }

  let { option = null, active = false, children, onevent }: Props = $props();

  let showInput = $state(false);
  let updatedData: any;
  let valueUpdated = false;
  let genericInputElem: GenericInput = $state();
  let actionParserLabel = $state(option?.actionParserLabel ?? '');

  // Resync when the parent passes a new option reference (e.g. after reset/external update)
  $effect(() => {
    actionParserLabel = option?.actionParserLabel ?? '';
  });

  onMount(() => {
    showInput = !!option?.isNewlyAdded;
  });

  const onClickEdit = (): void => {
    showInput = true;
  };

  const onChange = (detail: any): void => {
    updatedData = detail;
    valueUpdated = true;
  };

  const hideInput = (): void => {
    showInput = false;
    updatedData = undefined;
    valueUpdated = false;
  };

  const onClickCancel = (): void => {
    option?.restorePreviousState();
    option?.inputData?.resetIsValid();
    hideInput();
    actionParserLabel = option?.actionParserLabel ?? '';
    onevent?.({ type: 'edit-cancel' });
  };

  const validate = (): boolean => {
    if (!option?.inputData?.isRequired || !valueUpdated) return true;

    genericInputElem?.validate(updatedData);
    return !!option?.inputData?.isValid;
  };

  const onClickSave = (): void => {
    valueUpdated && option?.setValue(updatedData);
    if (validate()) {
      option?.inputData?.setPreviousProps(option?.inputData?.props);
      hideInput();
    }
    actionParserLabel = option?.actionParserLabel ?? '';
    onevent?.({ type: 'edited', data: option });
  };

  const onClickReset = (): void => {
    option?.resetInputDataToOriginal();
    onevent?.({ type: 'edit-reset', data: option });
    hideInput();
    actionParserLabel = '';
  };
</script>

{#if active}
  <div class="comp-editable-list-row">
    {#if showInput}
      <div class="input-container">
        <GenericInput bind:this={genericInputElem} option={option?.inputData} onchange={onChange} />
        <div class="confirm-action">
          <ConfirmActions okLabel={Label.Save} oncancel={onClickCancel} onok={onClickSave} />
          {#if option?.showResetAction}
            <ResetAction value={option?.parsedTooltipValue} onclick={onClickReset} />
          {/if}
        </div>
      </div>
    {:else}
      <div class="hover-action" class:active={!option?.isReadonly}>
        {@render children?.()}

        <div class="icon">
          <Icon value="icon-edit" type={IconType.Button} onclick={onClickEdit} />
        </div>
      </div>
    {/if}
    {#if actionParserLabel}
      <div class="label">({actionParserLabel})</div>
    {/if}
  </div>
{:else}
  {@render children?.()}
{/if}

<style>
  .comp-editable-list-row {
    flex-basis: 80%;
    display: flex;
    justify-content: space-between;
  }
  .comp-editable-list-row .input-container {
    width: 100%;
    flex-basis: 75%;
  }
  .input-container .confirm-action {
    margin-top: 8px;
    display: flex;
  }
  .hover-action {
    display: flex;
    justify-content: space-between;
  }
  .hover-action.active {
    padding: 4px;
    flex-basis: 75%;
  }
  .hover-action .icon {
    display: flex;
    align-items: center;
    visibility: hidden;
  }
  .hover-action.active:hover {
    border-radius: 2px;
    background-color: #ffffff66;
    border-bottom: 1px solid rgba(0, 0, 0, 0.15);
  }
  .hover-action.active:hover .icon {
    visibility: visible;
  }
  .comp-editable-list-row .label {
    display: flex;
    align-items: flex-start;
    padding-top: 8px;
    font-size: 11px;
    line-height: 18px;
    color: var(--grey-medium);
  }
</style>
